import posthog from "posthog-js";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_KEY = "all3d_lead_email";

function safeLocalGet(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function safeLocalSet(key, value) {
  try { window.localStorage.setItem(key, value); } catch {}
}

function pickEmail(urlParams, utmContent) {
  const fromUrl = urlParams.get("email");
  if (fromUrl && EMAIL_RE.test(fromUrl)) return fromUrl;
  if (utmContent && EMAIL_RE.test(utmContent)) return utmContent;
  return null;
}

/**
 * Read UTM params + email from the current URL, persist them to localStorage,
 * register them as PostHog super-properties (so they ride along on every event),
 * and identify the visitor by email when available.
 *
 * Email is taken from `?email=` first, then `utm_content` if that value
 * looks like an email (ALL3D email campaigns put the lead's address there).
 *
 * Safe to call before consent / posthog load — URL context is still persisted,
 * but PostHog calls no-op until the main instance is loaded.
 * Safe to call repeatedly — only updates state when new URL data is present.
 */
export function captureMarketingContext() {
  if (typeof window === "undefined") return;

  const urlParams = new URLSearchParams(window.location.search);
  const updates = {};

  for (const key of UTM_KEYS) {
    const fromUrl = urlParams.get(key);
    if (fromUrl) {
      safeLocalSet(key, fromUrl);
      updates[key] = fromUrl;
    }
  }

  const utmContent = updates.utm_content || safeLocalGet("utm_content");
  const email = pickEmail(urlParams, utmContent);
  if (email) {
    safeLocalSet(EMAIL_KEY, email);
  }

  if (!window._posthogLoaded) return;

  try {
    const context = getStoredMarketingContext();
    if (Object.keys(context).length > 0) {
      posthog.register(context);
    }
    if (context.email) {
      posthog.identify(context.email, { email: context.email });
    }
  } catch {}
}

/**
 * Snapshot of stored marketing context (UTMs + email) for inclusion on
 * individual event payloads. Returned object only contains keys with values.
 */
export function getStoredMarketingContext() {
  const ctx = {};
  if (typeof window === "undefined") return ctx;
  for (const key of UTM_KEYS) {
    const v = safeLocalGet(key);
    if (v) ctx[key] = v;
  }
  const email = safeLocalGet(EMAIL_KEY);
  if (email) ctx.email = email;
  return ctx;
}
